class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      // WaterFun (QA DEV-13): skip a section that is missing from the response
      // (e.g. nothing was added) instead of throwing a TypeError.
      const html = parsedState.sections && parsedState.sections[section.id];
      const target = document.getElementById(section.id);
      if (!html || !target) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner !== null) target.innerHTML = inner;
    });

    this.syncMixedCartGuard();
    if (this.header) this.header.reveal();
    this.open();
  }

  // WaterFun (QA DEV-01): the re-rendered product block carries an invisible
  // .wf-cn-mixed-flag when the cart mixes pickup-only and shippable items;
  // the warning itself lives in its own slot ([data-wf-cn-guard]).
  // Warning state (styled in waterfun-modern.css): checkout hidden + disabled,
  // amber title, and the cart button becomes the main action, since the cart
  // page is where the order gets split.
  syncMixedCartGuard() {
    const mixed = !!this.notification.querySelector('#cart-notification-product .wf-cn-mixed-flag');
    this.notification.classList.toggle('wf-cn--mixed', mixed);

    const guard = this.notification.querySelector('[data-wf-cn-guard]');
    if (guard) guard.hidden = !mixed;

    const checkout = this.notification.querySelector('#cart-notification-form [name="checkout"]');
    if (checkout) checkout.disabled = mixed;

    const title = this.notification.querySelector('[data-wf-cn-title]');
    if (title) {
      if (!title.dataset.defaultText) title.dataset.defaultText = title.textContent.trim();
      title.textContent = mixed ? title.dataset.mixedText : title.dataset.defaultText;
    }

    // The cart button's text comes back from its section on every add, so
    // it only needs replacing in the mixed state.
    const cartButton = this.notification.querySelector('#cart-notification-button');
    if (cartButton && mixed) cartButton.textContent = 'לעגלה – לפיצול ההזמנה';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    // WaterFun (QA DEV-13): null when the selector isn't in the markup.
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
